import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';

import QuantitySourcePicker from './QuantitySourcePicker';

/**
 * QuantityLinkApp
 *
 * Mounts a single React root that manages the QuantitySourcePicker modal.
 * The expenditure table (plain JS) opens the picker by dispatching a
 * custom DOM event `quantitylink:open` with the following detail:
 *
 *   {
 *     expenseId:      number,
 *     sourcesUrl:     string,
 *     linkUrl:        string,
 *     unlinkUrl:      string,
 *     amountSlotIndex: number,   // optional, defaults to 0
 *   }
 *
 * After a successful link or unlink the app dispatches `quantitylink:changed`
 * so the table can reload.
 *
 * Deferred (create) mode: when the source is chosen before the expense exists
 * (e.g. the quick-add modal), the opener passes `deferred: true` and omits
 * `expenseId`/`linkUrl`. Instead of POSTing the link, the picker reports the
 * chosen source back via the `quantitylink:selected` event so the caller can
 * include it in the create payload.
 */
const QuantityLinkApp = () => {
    const [pickerState, setPickerState] = useState(null); // null = hidden

    const handleOpen = useCallback((e) => {
        const { expenseId, sourcesUrl, linkUrl, unlinkUrl, amountSlotIndex, venueId, currentLink, deferred } = e.detail || {};
        if (!sourcesUrl) return;
        // Edit mode performs the link immediately and needs the link endpoint.
        if (!deferred && (!expenseId || !linkUrl)) return;
        setPickerState({
            deferred: !!deferred,
            expenseId,
            sourcesUrl,
            linkUrl,
            unlinkUrl,
            amountSlotIndex: amountSlotIndex ?? 0,
            venueId: venueId ?? 0,
            currentLink: currentLink ?? null,
        });
    }, []);

    useEffect(() => {
        window.addEventListener('quantitylink:open', handleOpen);
        return () => window.removeEventListener('quantitylink:open', handleOpen);
    }, [handleOpen]);

    const handleLinked = useCallback(() => {
        setPickerState(null);
        window.dispatchEvent(new CustomEvent('quantitylink:changed'));
    }, []);

    const handleSelected = useCallback((selection) => {
        window.dispatchEvent(
            new CustomEvent('quantitylink:selected', {
                detail: {
                    amountSlotIndex: selection.amountSlotIndex ?? 0,
                    sourceType: selection.sourceType,
                    sourceId: selection.sourceId,
                    driver: selection.driverKey ?? null,
                    label: selection.label,
                    value: selection.value,
                },
            }),
        );
        setPickerState(null);
    }, []);

    const handleClose = useCallback(() => {
        setPickerState(null);
    }, []);

    if (!pickerState) return null;

    return (
        <QuantitySourcePicker
            sourcesUrl={pickerState.sourcesUrl}
            linkUrl={pickerState.linkUrl}
            amountSlotIndex={pickerState.amountSlotIndex}
            venueId={pickerState.venueId}
            currentLink={pickerState.currentLink}
            deferred={pickerState.deferred}
            onSelected={handleSelected}
            onLinked={handleLinked}
            onClose={handleClose}
        />
    );
};

const rootEl = document.getElementById('QuantityLinkApp');
if (rootEl) {
    ReactDOM.render(
        <App>
            <QuantityLinkApp />
        </App>,
        rootEl,
    );
}
